import {
  Badge,
  Button,
  LoadingState,
  PageHeader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, RefreshCw, RotateCcw } from 'lucide-react'
import { useState } from 'react'

import { useAuth } from '@/hooks/use-auth'
import {
  type RdSyncLog,
  useRdLastPayload,
  useRdSyncLogs,
} from '@/hooks/use-settings'

export const Route = createFileRoute('/_app/integracoes/rd-marketing/logs')({
  component: RdMarketingLogsPage,
})

type DirectionFilter = 'all' | 'inbound' | 'outbound'

const DIRECTION_LABELS: Record<DirectionFilter, string> = {
  all: 'Todos',
  inbound: '← Recebidos',
  outbound: '→ Enviados',
}

function RdMarketingLogsPage() {
  const { currentOrgId } = useAuth()
  const navigate = useNavigate()
  const orgId = currentOrgId

  const [direction, setDirection] = useState<DirectionFilter>('all')
  const [selectedLog, setSelectedLog] = useState<RdSyncLog | null>(null)

  const lastPayload = useRdLastPayload(orgId)
  const logs = useRdSyncLogs(orgId, {
    limit: 500,
    direction: direction === 'all' ? undefined : direction,
  })

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader
        title="Logs de sincronização"
        description="Histórico de leads recebidos e conversões enviadas ao RD Station."
        back={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Voltar para RD Station Marketing"
            onClick={() => void navigate({ to: '/integracoes/rd-marketing' })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        }
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={logs.isFetching}
            onClick={() => void logs.refetch()}
          >
            <RotateCcw
              className={`h-3.5 w-3.5 ${logs.isFetching ? 'animate-spin' : ''}`}
            />
            Atualizar
          </Button>
        }
      />

      <div className="flex-1 space-y-5 overflow-y-auto p-6">
        {/* Último payload recebido */}
        <section className="bg-card border-border rounded-xl border p-5">
          <div className="mb-3 flex items-center justify-between gap-4">
            <h3 className="font-semibold">Último payload recebido</h3>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => void lastPayload.refetch()}
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
          {lastPayload.data?.lastInboundAt ? (
            <>
              <p className="text-muted-foreground mb-3 text-xs">
                Recebido em:{' '}
                {new Date(lastPayload.data.lastInboundAt).toLocaleString(
                  'pt-BR'
                )}
              </p>
              <pre className="bg-muted max-h-56 overflow-auto rounded-md p-3 text-xs">
                {JSON.stringify(lastPayload.data.lastInboundPayload, null, 2)}
              </pre>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">
              Nenhum payload recebido ainda.
            </p>
          )}
        </section>

        {/* Logs */}
        <section className="bg-card border-border rounded-xl border p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h3 className="font-semibold">Registros</h3>
            {/* Direction filter */}
            <div className="flex gap-1 rounded-lg border p-0.5">
              {(Object.keys(DIRECTION_LABELS) as DirectionFilter[]).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirection(d)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    direction === d
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {DIRECTION_LABELS[d]}
                </button>
              ))}
            </div>
          </div>

          {logs.isLoading ? (
            <LoadingState />
          ) : !logs.data?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Nenhum log encontrado.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground border-border border-b">
                  <tr>
                    <th className="pb-2 text-left font-medium">Data</th>
                    <th className="pb-2 text-left font-medium">Direção</th>
                    <th className="pb-2 text-left font-medium">Tipo</th>
                    <th className="pb-2 text-left font-medium">Status</th>
                    <th className="pb-2 text-left font-medium">Erro</th>
                    <th className="pb-2 text-left font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-border divide-y">
                  {logs.data.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="text-muted-foreground py-2.5 pr-4 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="py-2.5 pr-4 font-medium">
                        {log.direction === 'inbound' ? (
                          <span className="text-info">← recebido</span>
                        ) : (
                          <span className="text-ai">→ enviado</span>
                        )}
                      </td>
                      <td className="text-muted-foreground py-2.5 pr-4">
                        {log.entityType ?? '—'}
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge
                          variant={
                            log.status === 'success'
                              ? 'default'
                              : log.status === 'skipped'
                                ? 'secondary'
                                : 'destructive'
                          }
                          className="text-2xs"
                        >
                          {log.status}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground max-w-48 truncate py-2.5 pr-4">
                        {log.error ?? '—'}
                      </td>
                      <td className="py-2.5">
                        <ExternalLink className="text-muted-foreground h-3.5 w-3.5" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Log detail sheet */}
      <Sheet
        open={!!selectedLog}
        onOpenChange={(open) => {
          if (!open) setSelectedLog(null)
        }}
      >
        <SheetContent className="w-full max-w-xl overflow-y-auto sm:max-w-xl">
          <SheetHeader className="mb-4">
            <SheetTitle>Detalhes do log</SheetTitle>
          </SheetHeader>
          {selectedLog && (
            <div className="space-y-4 px-4 pb-4 text-sm">
              <div className="grid grid-cols-2 gap-y-2 text-xs">
                <span className="text-muted-foreground">Data</span>
                <span>
                  {new Date(selectedLog.createdAt).toLocaleString('pt-BR')}
                </span>
                <span className="text-muted-foreground">Direção</span>
                <span>
                  {selectedLog.direction === 'inbound'
                    ? '← Recebido'
                    : '→ Enviado'}
                </span>
                <span className="text-muted-foreground">Tipo</span>
                <span>{selectedLog.entityType ?? '—'}</span>
                <span className="text-muted-foreground">Entidade ID</span>
                <span className="font-mono break-all">
                  {selectedLog.entityId ?? '—'}
                </span>
                <span className="text-muted-foreground">Status</span>
                <span>
                  <Badge
                    variant={
                      selectedLog.status === 'success'
                        ? 'default'
                        : selectedLog.status === 'skipped'
                          ? 'secondary'
                          : 'destructive'
                    }
                    className="text-2xs"
                  >
                    {selectedLog.status}
                  </Badge>
                </span>
              </div>

              {selectedLog.error && (
                <div>
                  <p className="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                    Erro
                  </p>
                  <p className="text-destructive text-xs break-words whitespace-pre-wrap">
                    {selectedLog.error}
                  </p>
                </div>
              )}

              {selectedLog.request != null && (
                <div>
                  <p className="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                    Payload / Request
                  </p>
                  <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 text-xs">
                    {JSON.stringify(selectedLog.request, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.response != null && (
                <div>
                  <p className="text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase">
                    Response
                  </p>
                  <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 text-xs">
                    {JSON.stringify(selectedLog.response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
